/**
 * The five GPU-facing FX layers. Each is exactly ONE InstancedMesh — one draw call — and
 * all of them share one atlas texture.
 *
 *   ParticleLayer  billboarded quads with ballistic motion (soft / spray / hard)
 *   DecalLayer     ground-aligned quads in a ring buffer (splats, tyre marks)
 *   QuadLayer      arbitrarily oriented quads (contact shadows, wheel-spin blur)
 *   TextLayer      billboarded glyph quads for 3D-anchored pop text
 *   ScreenLayer    three camera-locked quads: flash + near-miss whoosh + speed streaks
 *
 * SIMULATION vs PRESENTATION (ARCHITECTURE §2, §7.3)
 *   step(dt)  advances state. Called ONLY from the fx fixed-step pump. dt is always 1/60.
 *   sync(...) writes matrices/attributes. Presentation only, never mutates sim state, and is
 *             a pure function of the state left by step() plus the camera pose.
 *
 * ALLOCATION (ARCHITECTURE N9)
 *   Every record is preallocated in ActiveList at boot. sync() reuses module-scope scratch
 *   objects. Nothing in step()/sync() allocates.
 */

import * as THREE from 'three';
import { ActiveList } from '../core/pool.js';
import { clamp01, lerp, ease } from '../core/mathx.js';
import { makeQuadGeometry } from './material.js';
import { FX } from './tuning.js';

// ------------------------------------------------------------------ scratch
const _m = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _q2 = new THREE.Quaternion();
const _v = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _sc = new THREE.Vector3();
const _AXIS_Z = new THREE.Vector3(0, 0, 1);

export const PFLAG = Object.freeze({
  GROUND: 1,     // collide with p.gy
  STICK: 2,      // on landing, hand off to the decal layer and die
  BOUNCE: 4,     // on landing, bounce instead of dying
  STREAK: 8,     // stretch the quad along screen-space velocity
});

function makeParticle() {
  return {
    x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0,
    life: 0, maxLife: 1,
    s0: 0.1, s1: 0.1,
    r: 1, g: 1, b: 1,
    a0: 1, a1: 0, fadeIn: 0.08,
    spin: 0, spinRate: 0,
    drag: 0, grav: 0,
    rect: 0,           // sprite index
    flags: 0,
    gy: 0,             // ground height for this particle
    stretch: 0,
    // Spawn position on the ground plane. Only STICK particles read it (fx `_stickDecal`
    // confines stains to FX.decal.maxThrow of where the material was thrown from), but it
    // is written for every particle in _reset so no spawn path can forget it.
    ox: 0, oz: 0,
  };
}

// ==================================================================== particles

export class ParticleLayer {
  /**
   * @param {THREE.Material} material patched clone
   * @param {Float32Array} spriteRects atlas rects
   */
  constructor(name, material, spriteRects, capacity, renderOrder) {
    this.name = name;
    this.capacity = capacity;
    this.rects = spriteRects;
    this.list = new ActiveList(capacity, makeParticle);
    this.geometry = makeQuadGeometry(capacity);
    this.mesh = new THREE.InstancedMesh(this.geometry, material, capacity);
    this.mesh.name = 'fx.' + name;
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = false;
    this.mesh.receiveShadow = false;
    this.mesh.renderOrder = renderOrder;
    this.mesh.count = 0;
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.aColor = this.geometry.getAttribute('aFxColor');
    this.aAlpha = this.geometry.getAttribute('aFxAlpha');
    this.aRect = this.geometry.getAttribute('aFxRect');
    /** set by FxSystem: (p) => void, called when a STICK particle lands */
    this.onStick = null;
    this.spawned = 0;
  }

  get count() { return this.list.count; }

  spawn() {
    const p = this.list.spawn();
    if (p) this.spawned++;
    return p;
  }

  step(dt) {
    const gEps = FX.physics.groundEpsilon;
    const rest = FX.physics.bounceRestitution;
    const fric = FX.physics.bounceFriction;
    const settle = FX.physics.settleSpeed;
    const onStick = this.onStick;
    const items = this.list.items;
    for (let i = this.list.count - 1; i >= 0; i--) {
      const p = items[i];
      p.life -= dt;
      if (p.life <= 0) { this.list.retire(i); continue; }

      p.vy -= p.grav * dt;
      if (p.drag > 0) {
        let d = 1 - p.drag * dt;
        if (d < 0) d = 0;
        p.vx *= d; p.vy *= d; p.vz *= d;
      }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.z += p.vz * dt;
      p.spin += p.spinRate * dt;

      if ((p.flags & PFLAG.GROUND) && p.y <= p.gy + gEps) {
        if (p.flags & PFLAG.STICK) {
          if (onStick) onStick(p);
          this.list.retire(i);
          continue;
        }
        if (p.flags & PFLAG.BOUNCE) {
          p.y = p.gy + gEps;
          const sp = Math.sqrt(p.vx * p.vx + p.vy * p.vy + p.vz * p.vz);
          if (sp < settle) {
            p.vx = 0; p.vy = 0; p.vz = 0;
            p.flags &= ~PFLAG.BOUNCE;
            p.grav = 0;
          } else {
            p.vy = Math.abs(p.vy) * rest;
            p.vx *= fric; p.vz *= fric;
            p.spinRate *= fric;
          }
        } else {
          this.list.retire(i);
          continue;
        }
      }
    }
  }

  /**
   * @param {THREE.Quaternion} camQuat  camera world rotation (billboard basis)
   * @param {THREE.Quaternion} camInv   inverse of camQuat, for screen-space velocity
   */
  sync(camQuat, camInv) {
    const n = this.list.count;
    const items = this.list.items;
    const col = this.aColor.array, alp = this.aAlpha.array, rct = this.aRect.array;
    const rects = this.rects;
    const mesh = this.mesh;

    for (let i = 0; i < n; i++) {
      const p = items[i];
      const t = 1 - p.life / p.maxLife;
      const size = lerp(p.s0, p.s1, t);
      let a = lerp(p.a0, p.a1, t);
      if (p.fadeIn > 0 && t < p.fadeIn) a *= t / p.fadeIn;

      if (p.flags & PFLAG.STREAK) {
        _v.set(p.vx, p.vy, p.vz);
        const sp = _v.length();
        _v.applyQuaternion(camInv);
        const ang = Math.atan2(_v.y, _v.x) - Math.PI * 0.5;
        _q.setFromAxisAngle(_AXIS_Z, ang);
        _sc.set(size, size + sp * p.stretch, 1);
      } else {
        _q.setFromAxisAngle(_AXIS_Z, p.spin);
        _sc.set(size, size, 1);
      }
      _q2.copy(camQuat).multiply(_q);
      _v2.set(p.x, p.y, p.z);
      _m.compose(_v2, _q2, _sc);
      mesh.setMatrixAt(i, _m);

      const i3 = i * 3, i4 = i * 4, r4 = p.rect * 4;
      col[i3] = p.r; col[i3 + 1] = p.g; col[i3 + 2] = p.b;
      alp[i] = a < 0 ? 0 : a;
      rct[i4] = rects[r4]; rct[i4 + 1] = rects[r4 + 1];
      rct[i4 + 2] = rects[r4 + 2]; rct[i4 + 3] = rects[r4 + 3];
    }

    mesh.count = n;
    // An InstancedMesh with count 0 is still submitted and still costs a draw call.
    // Five fx layers are empty in most frames; the build is 343/320 on draws.
    mesh.visible = n > 0;
    if (n > 0) {
      mesh.instanceMatrix.needsUpdate = true;
      this.aColor.needsUpdate = true;
      this.aAlpha.needsUpdate = true;
      this.aRect.needsUpdate = true;
    }
  }

  clear() { this.list.clear(); this.mesh.count = 0; }

  dispose() {
    this.geometry.dispose();
    if (this.mesh.material) this.mesh.material.dispose();
    this.mesh.dispose();
  }
}

// ==================================================================== decals

/**
 * Ground decals. A plain ring buffer rather than an ActiveList: decals are retired by AGE
 * (oldest evicted when full), which a swap-remove list cannot express, and a fixed ring
 * means the instance order never changes so the GPU upload is a straight overwrite.
 */
export class DecalLayer {
  constructor(material, spriteRects, capacity, renderOrder) {
    this.capacity = capacity;
    this.rects = spriteRects;
    this.cursor = 0;
    this.live = 0;
    this.laid = 0;

    this.items = new Array(capacity);
    for (let i = 0; i < capacity; i++) {
      this.items[i] = {
        x: 0, y: 0, z: 0,
        nx: 0, ny: 1, nz: 0,
        tx: 1, ty: 0, tz: 0,     // tangent (long axis)
        w: 1, h: 1,
        r: 1, g: 1, b: 1,
        a: 0, life: 0, maxLife: 1, fadeTail: 0.4,
        rect: 0,
      };
    }

    this.geometry = makeQuadGeometry(capacity);
    this.mesh = new THREE.InstancedMesh(this.geometry, material, capacity);
    this.mesh.name = 'fx.decals';
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = renderOrder;
    this.mesh.count = 0;
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.aColor = this.geometry.getAttribute('aFxColor');
    this.aAlpha = this.geometry.getAttribute('aFxAlpha');
    this.aRect = this.geometry.getAttribute('aFxRect');
    this._dirty = true;
  }

  /** Grab the next slot, evicting the oldest if we are full. */
  next() {
    const d = this.items[this.cursor];
    this.cursor = (this.cursor + 1) % this.capacity;
    if (this.live < this.capacity) this.live++;
    this.laid++;
    return d;
  }

  step(dt) {
    const n = this.live;
    for (let i = 0; i < n; i++) {
      const d = this.items[i];
      if (d.life > 0) d.life -= dt;
    }
    this._dirty = true;
  }

  sync() {
    const n = this.live;
    const col = this.aColor.array, alp = this.aAlpha.array, rct = this.aRect.array;
    const rects = this.rects;
    const el = this.mesh.instanceMatrix.array;

    for (let i = 0; i < n; i++) {
      const d = this.items[i];
      const o = i * 16;
      let a = 0;
      if (d.life > 0) {
        const t = 1 - d.life / d.maxLife;
        a = d.a;
        if (t > 1 - d.fadeTail) a *= clamp01((1 - t) / d.fadeTail);
        if (t < 0.05) a *= t / 0.05;
      }
      if (a <= 0.002) {
        // Collapsed to a point: still one instance, but zero fragments.
        for (let k = 0; k < 16; k++) el[o + k] = 0;
        el[o + 15] = 1;
        alp[i] = 0;
        continue;
      }

      // Basis: local +X → tangent, +Z → surface normal, +Y = n × t.
      const tx = d.tx, ty = d.ty, tz = d.tz;
      const nx = d.nx, ny = d.ny, nz = d.nz;
      const bx = ny * tz - nz * ty;
      const by = nz * tx - nx * tz;
      const bz = nx * ty - ny * tx;
      const w = d.w, h = d.h;
      el[o + 0] = tx * w; el[o + 1] = ty * w; el[o + 2] = tz * w; el[o + 3] = 0;
      el[o + 4] = bx * h; el[o + 5] = by * h; el[o + 6] = bz * h; el[o + 7] = 0;
      el[o + 8] = nx; el[o + 9] = ny; el[o + 10] = nz; el[o + 11] = 0;
      el[o + 12] = d.x; el[o + 13] = d.y; el[o + 14] = d.z; el[o + 15] = 1;

      const i3 = i * 3, i4 = i * 4, r4 = d.rect * 4;
      col[i3] = d.r; col[i3 + 1] = d.g; col[i3 + 2] = d.b;
      alp[i] = a;
      rct[i4] = rects[r4]; rct[i4 + 1] = rects[r4 + 1];
      rct[i4 + 2] = rects[r4 + 2]; rct[i4 + 3] = rects[r4 + 3];
    }

    this.mesh.count = n;
    this.mesh.visible = n > 0;
    if (n > 0) {
      this.mesh.instanceMatrix.needsUpdate = true;
      this.aColor.needsUpdate = true;
      this.aAlpha.needsUpdate = true;
      this.aRect.needsUpdate = true;
    }
    this._dirty = false;
  }

  clear() {
    for (let i = 0; i < this.capacity; i++) this.items[i].life = 0;
    this.live = 0; this.cursor = 0;
    this.mesh.count = 0;
  }

  dispose() {
    this.geometry.dispose();
    if (this.mesh.material) this.mesh.material.dispose();
    this.mesh.dispose();
  }
}

// ==================================================================== oriented quads

/**
 * Arbitrarily oriented, arbitrarily sized quads written straight into the instance matrix.
 *
 * Two consumers, one mesh, one draw call:
 *   · CONTACT SHADOWS — ground-aligned, normal +Y, long axis along the object's heading.
 *   · WHEEL-SPIN BLUR — vertical, normal along the axle, so it pins to the wheel face.
 *
 * The buffer is split:
 *   [0, staticN)                 written once, by `pushStatic`. Parked cars, street
 *                                furniture — anything that never moves. Uploaded only when
 *                                it changes.
 *   [staticN, staticN + dynN)    refilled from scratch every frame by `push`.
 * That split is the whole reason this is not a ring buffer: 610 parked cars must not cost
 * 610 matrix writes a frame, and a moving truck must not leave a trail of stale shadows.
 *
 * The dynamic region starts at `staticN` and NOT at a fixed reserved base. A fixed base of
 * 1200 meant `mesh.count` was 1200 + n every frame, so three re-uploaded the whole 1700 x
 * 64-byte instance matrix (108 kB) each frame to move ~150 quads, and rasterised 1200
 * degenerate instances for nothing. Consequence of the packed layout: `pushStatic` is a
 * SETUP call. If one arrives mid-frame it lands on a dynamic slot, which is corrected on
 * the next `beginFrame`.
 *
 * Nothing here is simulation. Every instance is derived from state the owning subsystem
 * already advanced in its own fixed step, so this is a pure presentation pass (§2).
 */
export class QuadLayer {
  constructor(name, material, spriteRects, capacity, staticCapacity, renderOrder) {
    this.name = name;
    this.capacity = capacity;
    /** hard cap on the persistent region, so a runaway caller cannot starve the dynamics */
    this.staticCapacity = Math.min(staticCapacity, capacity);
    this.rects = spriteRects;
    this.staticN = 0;
    this.dynBase = 0;
    this.dynN = 0;
    this.dropped = 0;

    this.geometry = makeQuadGeometry(capacity);
    this.mesh = new THREE.InstancedMesh(this.geometry, material, capacity);
    this.mesh.name = 'fx.' + name;
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = false;
    this.mesh.receiveShadow = false;
    this.mesh.renderOrder = renderOrder;
    this.mesh.count = 0;
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.aColor = this.geometry.getAttribute('aFxColor');
    this.aAlpha = this.geometry.getAttribute('aFxAlpha');
    this.aRect = this.geometry.getAttribute('aFxRect');
  }

  /** Called once per frame before any `push`. */
  beginFrame() { this.dynBase = this.staticN; this.dynN = 0; }

  /**
   * @param {number} i          instance slot
   * @param {number} px,py,pz   centre
   * @param {number} ux,uy,uz   half-extent along the quad's local +X (already scaled)
   * @param {number} vx,vy,vz   half-extent along the quad's local +Y (already scaled)
   */
  _write(i, px, py, pz, ux, uy, uz, vx, vy, vz, r, g, b, a, rect) {
    const el = this.mesh.instanceMatrix.array;
    const o = i * 16;
    // PlaneGeometry(1,1) spans ±0.5, so the basis columns are the FULL extents.
    el[o + 0] = ux * 2; el[o + 1] = uy * 2; el[o + 2] = uz * 2; el[o + 3] = 0;
    el[o + 4] = vx * 2; el[o + 5] = vy * 2; el[o + 6] = vz * 2; el[o + 7] = 0;
    // +Z is u x v — never sampled by the shader, but three needs a sane third column.
    el[o + 8] = uy * vz - uz * vy;
    el[o + 9] = uz * vx - ux * vz;
    el[o + 10] = ux * vy - uy * vx;
    el[o + 11] = 0;
    el[o + 12] = px; el[o + 13] = py; el[o + 14] = pz; el[o + 15] = 1;

    const i3 = i * 3, i4 = i * 4, r4 = rect * 4;
    const col = this.aColor.array, alp = this.aAlpha.array, rct = this.aRect.array;
    col[i3] = r; col[i3 + 1] = g; col[i3 + 2] = b;
    alp[i] = a;
    rct[i4] = this.rects[r4]; rct[i4 + 1] = this.rects[r4 + 1];
    rct[i4 + 2] = this.rects[r4 + 2]; rct[i4 + 3] = this.rects[r4 + 3];
  }

  push(px, py, pz, ux, uy, uz, vx, vy, vz, r, g, b, a, rect) {
    const i = this.dynBase + this.dynN;
    if (i >= this.capacity) { this.dropped++; return false; }
    this.dynN++;
    this._write(i, px, py, pz, ux, uy, uz, vx, vy, vz, r, g, b, a, rect);
    return true;
  }

  /** @returns the slot index, or -1 when the static region is full. */
  pushStatic(px, py, pz, ux, uy, uz, vx, vy, vz, r, g, b, a, rect) {
    const i = this.staticN;
    if (i >= this.staticCapacity) { this.dropped++; return -1; }
    this.staticN++;
    this._write(i, px, py, pz, ux, uy, uz, vx, vy, vz, r, g, b, a, rect);
    return i;
  }

  clearStatic() {
    for (let i = 0; i < this.staticN; i++) this.aAlpha.array[i] = 0;
    this.staticN = 0;
  }

  sync() {
    const end = this.dynBase + this.dynN;
    this.mesh.count = end;
    this.mesh.visible = end > 0;
    if (end > 0) {
      this.mesh.instanceMatrix.needsUpdate = true;
      this.aColor.needsUpdate = true;
      this.aAlpha.needsUpdate = true;
      this.aRect.needsUpdate = true;
    }
  }

  clear() {
    this.clearStatic();
    this.dynBase = 0;
    this.dynN = 0;
    this.mesh.count = 0;
  }

  /** The material is SHARED with the decal layer (that is the point) — it disposes it. */
  dispose() {
    this.geometry.dispose();
    this.mesh.dispose();
  }
}

// ==================================================================== pop text

function makeText() {
  return {
    x: 0, y: 0, z: 0,
    life: 0, maxLife: 1, rise: 1,
    /** when > 0 the rise is measured in SCREEN pixels instead of world metres */
    risePx: 0,
    r: 1, g: 1, b: 1,
    px: 1,
    /**
     * Optional chassis-relative anchor. `follow` non-zero means FxSystem re-derives x/y/z
     * from ctx.player every frame using (fx = lateral, fy = up, fz = forward) in the
     * vehicle's own basis. A pop that is pinned to a world point is gone in 0.2 s at
     * 94 km/h — it is still 3-D, still not a corner HUD element, but nobody ever sees it.
     */
    follow: 0, fx: 0, fy: 0, fz: 0,
    /**
     * ROUND 10. `lane` is the vertical band index this pop was granted at spawn (see
     * FX.text.lanePx) — two callouts near the same anchor never share one. `ground` non-zero
     * asks FxSystem._groundPass for a contact ellipse on the road beneath the anchor, which
     * is what stops a floating number reading as pasted on.
     */
    lane: 0, ground: 0,
    n: 0,
    slots: new Int16Array(FX.capacity.maxGlyphs),
    advs: new Float32Array(FX.capacity.maxGlyphs),
    total: 0,
    wobble: 0,
  };
}

export class TextLayer {
  constructor(material, atlas, capacity, glyphCapacity, renderOrder) {
    this.atlas = atlas;
    this.list = new ActiveList(capacity, makeText);
    this.glyphCapacity = glyphCapacity;
    this.geometry = makeQuadGeometry(glyphCapacity);
    this.mesh = new THREE.InstancedMesh(this.geometry, material, glyphCapacity);
    this.mesh.name = 'fx.poptext';
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = renderOrder;
    this.mesh.count = 0;
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.aColor = this.geometry.getAttribute('aFxColor');
    this.aAlpha = this.geometry.getAttribute('aFxAlpha');
    this.aRect = this.geometry.getAttribute('aFxRect');
    this.glyphs = 0;
    this.spawned = 0;
  }

  get count() { return this.list.count; }

  /**
   * @returns the record, already laid out, or null if the pool is full.
   *
   * ROUND 10 — "NEVER RENDER A PARTIAL STRING."
   *
   * `cone-slalom` shipped `THREADING THE NEED`, and a critic read it as the world-anchored
   * text being z-clipped by the truck body. It is not: `fx.trail`'s clone is authored
   * `depthTest: false`, so pop text cannot be occluded by anything. The cause was here —
   * `maxGlyphs` was **18** and `THREADING THE NEEDLE` is **20 characters**, so `push` silently
   * cut the last two and the game shipped a typo. `gameplay` owns the string, `fx` owns the
   * buffer, and nothing anywhere logged.
   *
   * `maxGlyphs` now covers every string either subsystem emits with room to spare, and the
   * clip below is a hard error instead of a silent truncation: a string that does not fit is
   * dropped whole. A missing callout is a missed opportunity; a truncated one is a bug the
   * player can read.
   */
  push(str) {
    const max = FX.capacity.maxGlyphs;
    if (str.length > max) return null;
    const t = this.list.spawn();
    if (!t) return null;
    const atlas = this.atlas;
    let n = 0, total = 0;
    const len = str.length;
    for (let i = 0; i < len; i++) {
      const slot = atlas.glyphFor(str.charCodeAt(i));
      if (slot < 0) continue;
      const adv = atlas.glyphAdv[slot] + FX.text.tracking;
      t.slots[n] = slot;
      t.advs[n] = adv;
      total += adv;
      n++;
    }
    t.n = n;
    t.total = total;
    this.spawned++;
    return t;
  }

  step(dt) {
    const items = this.list.items;
    for (let i = this.list.count - 1; i >= 0; i--) {
      const t = items[i];
      t.life -= dt;
      if (t.life <= 0) this.list.retire(i);
    }
  }

  /**
   * @param {THREE.Camera} camera
   * @param {number} perPixel  world metres per screen pixel at 1 m distance
   */
  sync(camera, perPixel) {
    const items = this.list.items;
    const n = this.list.count;
    const col = this.aColor.array, alp = this.aAlpha.array, rct = this.aRect.array;
    const rects = this.atlas.glyphRects;
    const camQuat = camera.quaternion;
    const camPos = camera.position;
    const T = FX.text;

    // Camera basis for laying glyphs out along the screen X/Y axes.
    _v2.set(1, 0, 0).applyQuaternion(camQuat);   // right
    const rx = _v2.x, ry = _v2.y, rz = _v2.z;

    let gi = 0;
    for (let i = 0; i < n && gi < this.glyphCapacity; i++) {
      const t = items[i];
      // The second half of "never render a partial string": if this whole word will not fit
      // in what is left of the shared glyph buffer, skip it entirely rather than laying out
      // the first few letters and stopping. Skip, not break — a shorter pop behind it in the
      // list may still fit, and dropping a `+250` because a `COMBO PERDU` is live is worse.
      if (gi + t.n > this.glyphCapacity) continue;
      const age = t.maxLife - t.life;
      const prog = clamp01(age / t.maxLife);

      // Rise + overshoot pop, then fade out over the tail.
      const pop = age < T.popTime ? ease.outBack(age / T.popTime) : 1 + Math.sin((age - T.popTime) * 6.0) * 0.03;
      let a = 1;
      if (prog > 1 - T.fadeTail) a = clamp01((1 - prog) / T.fadeTail);
      if (age < 0.05) a *= age / 0.05;

      // Constant apparent size: scale with distance so 90 km/h does not shrink the payoff.
      // Distance is measured at the ANCHOR, not at the risen position, so both the glyph
      // size and a pixel-space rise stay constant for the whole life of the pop.
      let dx = t.x - camPos.x, dy = t.y - camPos.y, dz = t.z - camPos.z;
      let dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (dist < T.minDist) dist = T.minDist;
      if (dist > T.maxDist) dist = T.maxDist;
      const cellWorld = perPixel * dist * T.basePx * t.px * pop;

      // A rise authored in metres is 200 px at 2 m and 3 px at 60 m. `risePx` asks for a
      // fixed screen travel instead, which is what "rises ~40 px" actually means.
      const riseWorld = t.risePx > 0 ? perPixel * dist * t.risePx : t.rise;
      // The lane offset is NOT eased — it is a fixed screen-space band, so two callouts that
      // spawn a frame apart never cross each other on their way up.
      const laneWorld = t.lane > 0 ? perPixel * dist * (t.lane * T.lanePx) : 0;
      const wy = t.y + laneWorld + riseWorld * ease.outCubic(prog) + Math.sin(age * 7 + t.wobble) * 0.03;

      const start = -t.total * 0.5 * cellWorld;
      let cursor = start;
      for (let k = 0; k < t.n && gi < this.glyphCapacity; k++) {
        const adv = t.advs[k] * cellWorld;
        const ox = cursor + adv * 0.5;
        cursor += adv;

        _v.set(t.x + rx * ox, wy + ry * ox, t.z + rz * ox);
        _sc.set(cellWorld, cellWorld, 1);
        _m.compose(_v, camQuat, _sc);
        this.mesh.setMatrixAt(gi, _m);

        const i3 = gi * 3, i4 = gi * 4, r4 = t.slots[k] * 4;
        col[i3] = t.r; col[i3 + 1] = t.g; col[i3 + 2] = t.b;
        alp[gi] = a;
        rct[i4] = rects[r4]; rct[i4 + 1] = rects[r4 + 1];
        rct[i4 + 2] = rects[r4 + 2]; rct[i4 + 3] = rects[r4 + 3];
        gi++;
      }
    }

    this.glyphs = gi;
    this.mesh.count = gi;
    this.mesh.visible = gi > 0;
    if (gi > 0) {
      this.mesh.instanceMatrix.needsUpdate = true;
      this.aColor.needsUpdate = true;
      this.aAlpha.needsUpdate = true;
      this.aRect.needsUpdate = true;
    }
  }

  clear() { this.list.clear(); this.mesh.count = 0; this.glyphs = 0; }

  dispose() {
    this.geometry.dispose();
    if (this.mesh.material) this.mesh.material.dispose();
    this.mesh.dispose();
  }
}

// ==================================================================== screen

/**
 * Three camera-locked quads: instance 0 is the full-screen colour flash, 1 the radial
 * near-miss whoosh, 2 the speed streak overlay. Pinned in front of the near plane with
 * depthTest off, so this needs no post-processing stack (which `look` owns) and costs one
 * draw call whatever is active.
 */
export class ScreenLayer {
  constructor(material, spriteRects, whiteRect, linesRect, streakRect, renderOrder) {
    this.rects = spriteRects;
    this.whiteRect = whiteRect;
    this.linesRect = linesRect;
    this.streakRect = streakRect;
    this.geometry = makeQuadGeometry(3);
    this.mesh = new THREE.InstancedMesh(this.geometry, material, 3);
    this.mesh.name = 'fx.screen';
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = renderOrder;
    this.mesh.count = 0;
    this.aColor = this.geometry.getAttribute('aFxColor');
    this.aAlpha = this.geometry.getAttribute('aFxAlpha');
    this.aRect = this.geometry.getAttribute('aFxRect');
  }

  _put(i, pos, quat, w, h, color, alpha, rect) {
    _sc.set(w, h, 1);
    _m.compose(pos, quat, _sc);
    this.mesh.setMatrixAt(i, _m);
    const col = this.aColor.array, alp = this.aAlpha.array, rct = this.aRect.array;
    const i3 = i * 3, i4 = i * 4, r4 = rect * 4;
    col[i3] = color[0]; col[i3 + 1] = color[1]; col[i3 + 2] = color[2];
    alp[i] = alpha;
    rct[i4] = this.rects[r4]; rct[i4 + 1] = this.rects[r4 + 1];
    rct[i4 + 2] = this.rects[r4 + 2]; rct[i4 + 3] = this.rects[r4 + 3];
  }

  /**
   * @param {THREE.Camera} camera
   * @param {number} flashA   0..1
   * @param {Float32Array} flashColor linear rgb
   * @param {number} linesA   0..1   near-miss whoosh
   * @param {Float32Array} linesColor
   * @param {number} streakA  0..1   speed streak overlay
   * @param {Float32Array} streakColor
   */
  sync(camera, flashA, flashColor, linesA, linesColor, streakA, streakColor) {
    if (flashA <= 0.002 && linesA <= 0.002 && streakA <= 0.002) {
      this.mesh.count = 0; this.mesh.visible = false; return;
    }
    this.mesh.visible = true;

    const d = Math.max((camera.near || 0.25) * 2.2, 0.6);
    const fovR = (camera.fov || 60) * Math.PI / 180;
    const hh = Math.tan(fovR * 0.5) * d * 2.15;
    const hw = hh * (camera.aspect || 1.777);

    camera.getWorldDirection(_v);
    _v2.copy(camera.position).addScaledVector(_v, d);
    const q = camera.quaternion;

    // 0 — flat colour flash
    this._put(0, _v2, q, hw, hh, flashColor, flashA, this.whiteRect);
    // 1 — radial whoosh, squarish so the streaks stay radial not stretched
    const s = Math.max(hw, hh) * 1.25;
    this._put(1, _v2, q, s, s, linesColor, linesA, this.linesRect);
    // 2 — speed streaks, mapped 1:1 onto the frame so the sprite's clear centre IS the
    //     clear centre of the picture. See atlas.js paintSpeedStreak.
    this._put(2, _v2, q, hw, hh, streakColor, streakA, this.streakRect);

    this.mesh.count = 3;
    this.mesh.instanceMatrix.needsUpdate = true;
    this.aColor.needsUpdate = true;
    this.aAlpha.needsUpdate = true;
    this.aRect.needsUpdate = true;
  }

  dispose() {
    this.geometry.dispose();
    if (this.mesh.material) this.mesh.material.dispose();
    this.mesh.dispose();
  }
}
